import { AccountSettings } from "01Data/Account";
import { AppContext } from "01Data/AppData";
import { useQRDialog } from "02LoginPage/useQRDialog";
import PageTemplate from "04UserPages/00PageTemplate/PageTemplate";
import {
  BreadCrumbItem,
  BreadCrumbList,
} from "04UserPages/99Parts/BreadCrumbList";
import { ConfirmDialog } from "04UserPages/99Parts/ConfirmDialog";
import { useMessageDialog } from "97Parts/useMessageDialog";
import { Delete, Lock, Mail, Person, QrCode2 } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Button, styled } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import Paper from "@mui/material/Paper";
import { CognitoUser } from "amazon-cognito-identity-js";
import { useContext, useEffect, useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useUpdateMailDialog } from "./useUpdateMailDialog";
import { useUpdateNameDialog } from "./useUpdateNameDialog";
import { useUpdatePassDialog } from "./useUpdatePassDialog";

const ErrorText = styled("div")(({ theme }) => ({
  color: "#f00",
}));

const UserPageContents = () => {
  const a = useContext(AppContext).account;
  const navigate = useNavigate();
  const [settings, setSettings] = useState<AccountSettings>(
    new AccountSettings()
  );
  const [bShowConfirmDelete, setShowConfirmDelete] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorDetail, setErrorDetail] = useState("");

  const [loginUser, setLoginUser] = useState<CognitoUser | null>(null);
  const [mfaSecret, setMfaSecret] = useState("");
  const [mfaChanging, setMfaChanging] = useState(false);
  const { QRDialog, openQRDialog } = useQRDialog();
  const [dialogMessage, setDialogMessage] = useState("");
  const { MessageDialog, openMessageDialog } = useMessageDialog();

  const { UpdateNameDialog, openUpdateNameDialog } = useUpdateNameDialog();
  const { UpdatePassDialog, openUpdatePassDialog } = useUpdatePassDialog();
  const { UpdateMailDialog, openUpdateMailDialog } = useUpdateMailDialog();

  const [mfaAction, setMfaAction] = useState("");

  useEffect(() => {
    (async () => {
      const attr = await a.GetAttributes();
      setSettings(attr);
    })();
  }, [a]);

  useLayoutEffect(() => {
    (async () => {
      const p = await a.getPrefferedMfa();
      p === "SOFTWARE_TOKEN_MFA"
        ? setMfaAction("無効化")
        : setMfaAction("有効化");
    })();
  }, [a]);

  const handleToggleMfaSetting = async () => {
    const p = await a.getPrefferedMfa();
    const cognitoUser = await a.getCurrentUser();
    if (cognitoUser) {
      setMfaChanging(true);
      try {
        if (p === "SOFTWARE_TOKEN_MFA") {
          // MFA アクティブ → 非アクティブ
          await a.setPrefferedMfa("NOMFA");
          setDialogMessage(`MFA（多要素認証）を無効にしました。`);
          await openMessageDialog();
          setMfaAction(" 有効化");
        } else {
          // 非アクティブ → アクティブ
          setLoginUser(cognitoUser);
          const code = await a.setupTOTP(cognitoUser);
          setMfaSecret(code);
          const result = await openQRDialog();
          if (result === "ok") {
            setDialogMessage(`MFA（多要素認証）を有効にしました。`);
            await openMessageDialog();
            setMfaAction("無効化");
          }
        }
      } catch (error) {
        setErrorMessage("MFA設定変更に失敗しました。");
        setErrorDetail((error as Error).message);
      } finally {
        setMfaChanging(false);
      }
    } else {
      // hot reload 時のみ
      console.debug("CognitoUser not found");
    }
  };

  const handleDelete = async () => {
    try {
      await a.DeleteUser();
      setDialogMessage(`ユーザーを削除しました。`);
      await openMessageDialog();
      navigate("/login/");
    } catch (error) {
      console.log("ユーザー削除エラー");
      setShowConfirmDelete(false);
      console.error(error);
      setErrorMessage("ユーザー削除処理が失敗しました。");
      const messages = (error as Error).toString().split(":");
      if (0 < messages.length) {
        setErrorDetail(
          `詳細: ${messages.length === 1 ? messages[0].trim() : messages[1].trim()}`
        );
      }
    }
  };

  const handleUpdateName = async () => {
    const result = await openUpdateNameDialog();
    if (result === "ok") {
      setDialogMessage("ユーザー名を変更しました。");
      await openMessageDialog();
    }
  };

  const handleUpdatePass = async () => {
    const result = await openUpdatePassDialog();
    if (result === "ok") {
      setDialogMessage("パスワードを変更しました。");
      await openMessageDialog();
    }
  };

  const handleUpdateMailAddress = async () => {
    const result = await openUpdateMailDialog();
    if (result === "ok") {
      setDialogMessage(
        "メールアドレスを変更しました。再ログインしてください。"
      );
      await openMessageDialog();
      a.SignOut();
    }
  };

  return (
    <Paper sx={{ margin: { xs: "0", sm: "0 5% 0" }, padding: "5%" }}>
      <FormControl fullWidth>
        {errorMessage !== "" ? <ErrorText>{errorMessage}</ErrorText> : null}
        {errorDetail !== "" ? <ErrorText>{errorDetail}</ErrorText> : null}
        <Button
          aria-label="ユーザー名変更"
          variant="contained"
          color="primary"
          onClick={handleUpdateName}
          sx={{ m: 1 }}
          startIcon={<Person />}
        >
          <Box width={200}>
            {a.IsUserAdmin() ? "ユーザー情報変更" : "ユーザー名変更"}
          </Box>
        </Button>
        <Button
          aria-label="パスワード変更"
          variant="contained"
          color="primary"
          onClick={handleUpdatePass}
          sx={{ m: 1 }}
          startIcon={<Lock />}
        >
          <Box width={200}>パスワード変更</Box>
        </Button>
        <Button
          aria-label="メールアドレス変更"
          variant="contained"
          color="primary"
          onClick={handleUpdateMailAddress}
          sx={{ m: 1 }}
          startIcon={<Mail />}
        >
          <Box width={200}>メールアドレス変更</Box>
        </Button>
        <LoadingButton
          aria-label="MFA認証変更"
          variant="contained"
          color="primary"
          onClick={handleToggleMfaSetting}
          loading={mfaChanging}
          sx={{ m: 1 }}
          startIcon={<QrCode2 />}
        >
          <Box width={200}>MFA（多要素認証）{mfaAction}</Box>
        </LoadingButton>
        <LoadingButton
          aria-label="ユーザー削除"
          variant="contained"
          color="secondary"
          onClick={() => setShowConfirmDelete(true)}
          sx={{ m: 1 }}
          startIcon={<Delete />}
        >
          <Box width={200}>ユーザー削除</Box>
        </LoadingButton>
      </FormControl>
      <ConfirmDialog
        bShow={bShowConfirmDelete}
        title={`このユーザーを削除しますか？`}
        description=""
        onApply={handleDelete}
        onCancel={() => setShowConfirmDelete(false)}
        titleButtonApply="削除"
        titleButtonCancel="キャンセル"
      />
      {loginUser ? (
        <QRDialog user={loginUser} secret={mfaSecret} email={""} />
      ) : null}
      <MessageDialog message={dialogMessage} />
      <UpdateNameDialog account={settings}></UpdateNameDialog>
      <UpdatePassDialog></UpdatePassDialog>
      <UpdateMailDialog></UpdateMailDialog>
    </Paper>
  );
};

const UserSettingPage = () => {
  return (
    <PageTemplate title="ユーザー設定">
      <BreadCrumbList
        items={[
          new BreadCrumbItem("TOP", "/top/"),
          new BreadCrumbItem("ユーザー設定"),
        ]}
      />
      <UserPageContents />
    </PageTemplate>
  );
};

export default UserSettingPage;
